import { useForm, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetFooter, SheetClose } from "@/components/ui/sheet";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Loader2, Percent, Euro, Tag, Ticket } from "lucide-react";
import { useEffect, useMemo, useRef } from "react";
import type { PromotionBase } from "nautika-types";
import { useQueryClient } from "@tanstack/react-query";

// Schéma de validation pour les promotions
const promotionSchema = z.object({
  name: z.string().min(1, "Le nom est requis"),
  type: z.enum(["percentage", "fixed"]),
  value: z.number().min(1, "La valeur doit être supérieure à 0"),
  code: z.string().optional(),
});

type Promotion = z.infer<typeof promotionSchema>;
interface PromotionFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onPromotionCreated: (promotion: PromotionBase) => void;
  editingPromotion?: PromotionBase;
}

export function PromotionForm({ open, onOpenChange, onPromotionCreated, editingPromotion }: PromotionFormProps) {
  const userHasClearedName = useRef(false);
  const queryClient = useQueryClient();
  const form = useForm<Promotion>({
    resolver: zodResolver(promotionSchema),
    mode: "onBlur",
    reValidateMode: "onBlur",
    defaultValues: {
      name: "",
      type: "percentage",
      value: 0,
      code: "",
    },
  });

  const watchedType = useWatch({ control: form.control, name: "type" });
  const watchedValue = useWatch({ control: form.control, name: "value" });

  // Générer automatiquement le nom par défaut
  const defaultName = useMemo(() => {
    if (!watchedValue || watchedValue <= 0) return "";
    if (watchedType === "percentage") {
      return `Promotion de ${watchedValue}%`;
    } else {
      return `Réduction de ${watchedValue}€`;
    }
  }, [watchedType, watchedValue]);

  // Réinitialiser le formulaire quand editingPromotion change ou que le panneau s'ouvre
  useEffect(() => {
    if (open) {
      userHasClearedName.current = false; // Réinitialiser le flag
      if (editingPromotion) {
        form.reset({
          name: editingPromotion.name,
          type: editingPromotion.type,
          value: editingPromotion.value,
          code: editingPromotion.code || "",
        });
      } else {
        form.reset({
          name: "",
          type: "percentage",
          value: 0,
          code: "",
        });
      }
    }
  }, [editingPromotion, open, form]);

  // Mettre à jour automatiquement le nom si il est vide ou correspond au défaut précédent
  // mais seulement si l'utilisateur n'a pas intentionnellement effacé le nom
  useEffect(() => {
    const currentName = form.getValues("name");
    if (
      !editingPromotion &&
      !userHasClearedName.current &&
      (!currentName || currentName.startsWith("Promotion de") || currentName.startsWith("Réduction de"))
    ) {
      form.setValue("name", defaultName);
    }
  }, [defaultName, form, editingPromotion]);

  const createMutation = api.useMutation("post", "/promotions/", {
    onSuccess: (response) => {
      // Invalider le cache des promotions pour toutes les requêtes
      queryClient.invalidateQueries({ queryKey: ["get", "/promotions/"] });

      onPromotionCreated(response as PromotionBase);
      form.reset();
      onOpenChange(false);
      toast.success("Promotion créée avec succès");
    },
    onError: (error) => {
      toast.error(error.message || "Erreur lors de la création");
    },
  });

  const updateMutation = api.useMutation("put", "/promotions/{id}", {
    onSuccess: (response) => {
      // Invalider le cache des promotions pour toutes les requêtes
      queryClient.invalidateQueries({ queryKey: ["get", "/promotions/"] });

      onPromotionCreated(response as PromotionBase);
      form.reset();
      onOpenChange(false);
      toast.success("Promotion modifiée avec succès");
    },
    onError: (error) => {
      toast.error(error.message || "Erreur lors de la modification");
    },
  });

  const onSubmit = (data: Promotion) => {
    // Pour les montants fixes, on convertit en centimes
    const apiData = {
      ...data,
      name: data.name || defaultName, // Utiliser le nom par défaut si aucun nom n'est fourni
      value: data.type === "fixed" ? Math.round(data.value * 100) : data.value,
      code: data.code || undefined,
    };

    if (editingPromotion) {
      updateMutation.mutate({
        params: { path: { id: editingPromotion.id } },
        body: apiData,
      });
    } else {
      createMutation.mutate({ body: apiData });
    }
  };

  const displayValue = useMemo(() => {
    if (editingPromotion?.type === "fixed") {
      return editingPromotion.value * 100; // Convertir les centimes en euros pour l'affichage
    }
    return watchedValue;
  }, [editingPromotion, watchedValue]);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="h-[100vh] overflow-y-auto">
        <SheetHeader>
          <SheetTitle>{editingPromotion ? "Modifier la promotion" : "Nouvelle promotion"}</SheetTitle>
          <SheetDescription>Créez une promotion avec une valeur en pourcentage ou un montant fixe</SheetDescription>
        </SheetHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6 p-4">
            {/* Switch pour choisir le type */}
            <FormField
              control={form.control}
              name="type"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                  <div className="space-y-0.5">
                    <FormLabel className="text-base">Type de promotion</FormLabel>
                    <div className="text-sm text-muted-foreground">
                      {field.value === "percentage" ? "Pourcentage de réduction" : "Montant fixe de réduction"}
                    </div>
                  </div>
                  <FormControl>
                    <div className="flex items-center space-x-2">
                      <Percent className={`h-4 w-4 ${field.value === "percentage" ? "text-foreground" : "text-muted-foreground"}`} />
                      <Switch
                        checked={field.value === "fixed"}
                        onCheckedChange={(checked) => {
                          field.onChange(checked ? "fixed" : "percentage");
                        }}
                      />
                      <Euro className={`h-4 w-4 ${field.value === "fixed" ? "text-foreground" : "text-muted-foreground"}`} />
                    </div>
                  </FormControl>
                </FormItem>
              )}
            />

            {/* Valeur */}
            <FormField
              control={form.control}
              name="value"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {watchedType === "percentage" ? (
                      <>
                        <Percent className="mr-2 inline h-4 w-4" />
                        Pourcentage <span className="text-red-500">*</span>
                      </>
                    ) : (
                      <>
                        <Euro className="mr-2 inline h-4 w-4" />
                        Montant en euros <span className="text-red-500">*</span>
                      </>
                    )}
                  </FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      step={watchedType === "percentage" ? "1" : "0.01"}
                      min="0"
                      max={watchedType === "percentage" ? "100" : undefined}
                      placeholder={watchedType === "percentage" ? "Ex: 10" : "Ex: 5.00"}
                      value={editingPromotion && !form.formState.isDirty ? displayValue : field.value || ""}
                      onChange={(e) => {
                        const value = parseFloat(e.target.value) || 0;
                        field.onChange(value);
                      }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Nom */}
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    <Tag className="mr-2 inline h-4 w-4" />
                    Nom de la promotion
                  </FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Ex: Promotion de 10%"
                      {...field}
                      value={field.value !== undefined ? field.value : defaultName}
                      onFocus={(e) => {
                        const input = e.target as HTMLInputElement;
                        // Utiliser setTimeout pour s'assurer que la sélection se fait après le rendu
                        setTimeout(() => input.select(), 0);
                      }}
                      onChange={(e) => {
                        const newValue = e.target.value;
                        field.onChange(newValue);
                        // Détecter si l'utilisateur a intentionnellement effacé le contenu
                        if (newValue === "" && (field.value || defaultName)) {
                          userHasClearedName.current = true;
                        } else if (newValue !== "" && newValue !== defaultName) {
                          userHasClearedName.current = false;
                        }
                      }}
                    />
                  </FormControl>
                  <div className="text-sm text-muted-foreground">Laissez vide pour générer automatiquement un nom</div>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Code promo (optionnel) */}
            <FormField
              control={form.control}
              name="code"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    <Ticket className="mr-2 inline h-4 w-4" />
                    Code promo (optionnel)
                  </FormLabel>
                  <FormControl>
                    <Input placeholder="Ex: NAUTIKA-2025" {...field} />
                  </FormControl>
                  <div className="text-sm text-muted-foreground">Ce code pourra être utilisé par les parents pour appliquer automatiquement la promotion</div>
                  <FormMessage />
                </FormItem>
              )}
            />

            <SheetFooter>
              <Button type="submit" disabled={createMutation.isPending || updateMutation.isPending}>
                {(createMutation.isPending || updateMutation.isPending) && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {createMutation.isPending || updateMutation.isPending
                  ? editingPromotion
                    ? "Modification..."
                    : "Création..."
                  : editingPromotion
                    ? "Modifier cette promotion"
                    : "Créer cette promotion"}
              </Button>
              <SheetClose asChild>
                <Button variant="outline">Annuler</Button>
              </SheetClose>
            </SheetFooter>
          </form>
        </Form>
      </SheetContent>
    </Sheet>
  );
}
